import { setChannelCallPresence } from "@/lib/call-presence";
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "@/api/client";
import { watchMicrophoneAvailability } from "@/sip/watch-microphone-availability";

export type VoiceState =
  | "incoming"
  | "idle"
  | "starting"
  | "preparing"
  | "calling"
  | "ringing"
  | "connected"
  | "ended"
  | "error";
type Call = { id: string; state: VoiceState; reason: string | null; answeredAt?: string | null };
const root = `${API_BASE_URL}/v1/whatsapp-call/calls`;
async function jsonRequest(path: string, method: string, body?: unknown) {
  const response = await fetch(root + path, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body ? { body: JSON.stringify(body) } : {}),
    signal: AbortSignal.timeout(8000),
  });
  const data = await response.json();
  if (!response.ok)
    throw new Error(typeof data.error === "string" ? data.error : "voice_unavailable");
  return data;
}
function encodeAudio(frames: ArrayBuffer[]) {
  const bytes = new Uint8Array(frames.reduce((size, frame) => size + frame.byteLength, 0));
  let offset = 0;
  for (const frame of frames) {
    bytes.set(new Uint8Array(frame), offset);
    offset += frame.byteLength;
  }
  let raw = "";
  for (const byte of bytes) raw += String.fromCharCode(byte);
  return btoa(raw);
}
export function useWhatsAppVoice(organizationId?: string, inputDeviceId?: string) {
  const presenceKey = useRef(`whatsapp:${crypto.randomUUID()}`);
  const [historyCallId, setHistoryCallId] = useState<string | null>(null);
  const [endedCallId, setEndedCallId] = useState<string | null>(null);
  const [endedCallAnswered, setEndedCallAnswered] = useState(false);
  const generation = useRef(0);
  const [state, setState] = useState<VoiceState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [microphoneDisconnected, setMicrophoneDisconnected] = useState(false);
  const [microphoneNoticeDismissed, setMicrophoneNoticeDismissed] = useState(false);
  const localStream = useRef<MediaStream | null>(null);
  const getLocalStream = useCallback(() => localStream.current, []);
  const current = useRef<{ close: (hangup: boolean) => void; muted: boolean } | null>(null);
  useEffect(() => {
    setState("idle");
    setHistoryCallId(null);
    setEndedCallId(null);
    setEndedCallAnswered(false);
    setMuted(false);
    setMicrophoneDisconnected(false);
    setMicrophoneNoticeDismissed(false);
    setError(null);
    return () => {
      generation.current++;
      current.current?.close(true);
      current.current = null;
    };
  }, [organizationId]);

  const active = !["idle", "incoming", "ended", "error"].includes(state);
  useEffect(() => {
    const key = presenceKey.current;
    setChannelCallPresence(key, active);
    return () => setChannelCallPresence(key, false);
  }, [active]);
  const start = async (number: string, reason: string, incomingId?: string) => {
    if (current.current) return;
    const run = ++generation.current;
    setHistoryCallId(null);
    setEndedCallId(null);
    setEndedCallAnswered(false);
    setState("starting");
    setError(null);
    setMuted(false);
    setMicrophoneDisconnected(false);
    setMicrophoneNoticeDismissed(false);
    let callId: string | undefined;
    let context: AudioContext | undefined;
    let stream: MediaStream | undefined;
    let capture: AudioWorkletNode | undefined;
    let stopped = false;
    let microphoneAvailable = true;
    let stopMicrophoneWatch: (() => void) | undefined;
    let frames: ArrayBuffer[] = [];
    const playback = new Set<AudioBufferSourceNode>();
    const stopPlayback = () => {
      for (const source of playback) {
        source.stop();
        source.disconnect();
      }
      playback.clear();
    };
    const abort = new AbortController();
    const savedEnd = (result: { call: Call }) => {
      if (
        generation.current !== run ||
        result.call.id !== callId ||
        !["ended", "error"].includes(result.call.state)
      )
        return;
      setEndedCallId(result.call.id);
      setEndedCallAnswered(Boolean(result.call.answeredAt));
    };
    const close = (hangup: boolean) => {
      if (stopped) return;
      stopped = true;
      abort.abort();
      stopMicrophoneWatch?.();
      frames = [];
      stopPlayback();
      setMicrophoneDisconnected(false);
      if (capture) {
        capture.port.onmessage = null;
        capture.disconnect();
      }
      stream?.getTracks().forEach((track) => track.stop());
      if (localStream.current === stream) localStream.current = null;
      void context?.close().catch(() => {});
      if (hangup && (callId || incomingId))
        void jsonRequest(`/${callId || incomingId}`, "DELETE")
          .then(savedEnd)
          .catch(() => {});
      if (current.current === control) current.current = null;
    };
    const control = { close, muted: false };
    current.current = control;
    const fail = (code: string) => {
      if (stopped) return;
      close(Boolean(callId));
      setError(code);
      setState("error");
    };
    try {
      context = new AudioContext({ sampleRate: 16000 });
      await context.resume();
      if (stopped) return;
      if (context.sampleRate !== 16000) throw new Error("unsupported_audio_format");
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...(inputDeviceId ? { deviceId: { exact: inputDeviceId } } : {}),
        },
      });
      if (stopped) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      localStream.current = stream;
      stopMicrophoneWatch = watchMicrophoneAvailability(stream, (available) => {
        microphoneAvailable = available;
        frames = [];
        capture?.port.postMessage({ available });
        if (!available) stopPlayback();
        setMicrophoneDisconnected(!available);
        setMicrophoneNoticeDismissed(false);
      });
      await context.audioWorklet.addModule("/whatsapp-capture.js");
      if (stopped) return;
      capture = new AudioWorkletNode(context, "whatsapp-capture");
      capture.port.postMessage({ available: microphoneAvailable });
      const silent = context.createGain();
      silent.gain.value = 0;
      context
        .createMediaStreamSource(stream)
        .connect(capture)
        .connect(silent)
        .connect(context.destination);
      const result = (await (incomingId
        ? jsonRequest(`/${incomingId}/answer`, "POST")
        : jsonRequest("", "POST", { number, reason }))) as { call: Call };
      callId = result.call.id;
      if (stopped) {
        void jsonRequest(`/${callId}`, "DELETE").catch(() => {});
        return;
      }
      setHistoryCallId(callId);
      setState(result.call.state);
      let sequence = 0;
      let pending = false;
      // Keep capture independent of network RTT. A reply drains the accumulated
      // frames immediately; a 160ms round trip must not discard 60ms per upload.
      const upload = () => {
        if (stopped || pending || frames.length < 5) return;
        const batch = frames.splice(0, 10);
        pending = true;
        void jsonRequest(`/${callId}/audio`, "POST", {
          sequence: sequence++,
          audio: encodeAudio(batch),
        })
          .catch(() => fail("audio_connection_lost"))
          .finally(() => {
            pending = false;
            upload();
          });
      };
      capture.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (stopped) return;
        const frame = event.data;
        if (control.muted || !microphoneAvailable) new Float32Array(frame).fill(0);
        frames.push(frame);
        if (frames.length > 20) frames.shift();
        upload();
      };
      const response = await fetch(`${root}/${callId}/media`, {
        credentials: "include",
        signal: abort.signal,
      });
      if (!response.ok || !response.body) throw new Error("audio_connection_lost");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let scheduled = context.currentTime + 0.06;
      while (!stopped) {
        const result = await reader.read();
        if (result.done) break;
        buffer += decoder.decode(result.value, { stream: true });
        if (buffer.length > 256_000) throw new Error("invalid_audio");
        let newline: number;
        while ((newline = buffer.indexOf("\n")) >= 0) {
          const event = JSON.parse(buffer.slice(0, newline));
          buffer = buffer.slice(newline + 1);
          if (event.type === "state") {
            const call = event.call as Call;
            if (call.id !== callId) continue;
            setState(call.state);
            if (call.state === "ended" || call.state === "error") {
              if (call.state === "error") setError(call.reason ?? "voice_unavailable");
              void jsonRequest(`/${callId}`, "GET")
                .then(savedEnd)
                .catch(() => {});
              close(false);
              break;
            }
          } else if (
            event.type === "audio" &&
            typeof event.payload === "string" &&
            event.payload.length <= 1800
          ) {
            if (!microphoneAvailable) continue;
            const bytes = Uint8Array.from(atob(event.payload), (char) => char.charCodeAt(0));
            if (bytes.byteLength !== 1280) continue;
            const samples = new Float32Array(bytes.buffer);
            const audio = context.createBuffer(1, samples.length, 16000);
            audio.copyToChannel(samples, 0);
            const source = context.createBufferSource();
            source.buffer = audio;
            source.connect(context.destination);
            const now = context.currentTime;
            if (scheduled < now || scheduled > now + 0.3) scheduled = now + 0.06;
            playback.add(source);
            source.onended = () => {
              playback.delete(source);
              source.disconnect();
            };
            source.start(scheduled);
            scheduled += audio.duration;
          }
        }
      }
      if (!stopped) fail("audio_connection_lost");
    } catch (error) {
      fail(
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "microphone_denied"
          : error instanceof Error
            ? error.message
            : "voice_unavailable",
      );
    }
  };
  return {
    state,
    getLocalStream,
    callId: historyCallId,
    endedCallId,
    endedCallAnswered,
    error,
    muted,
    microphoneDisconnected,
    microphoneNoticeOpen: microphoneDisconnected && !microphoneNoticeDismissed,
    dismissMicrophoneNotice: () => setMicrophoneNoticeDismissed(true),
    active,
    dial: (number: string, reason: string) => start(number, reason),
    answer: (id: string) => start("", "", id),
    hangup: () => {
      current.current?.close(true);
      setState("ended");
    },
    toggleMute: () => {
      if (current.current) {
        current.current.muted = !current.current.muted;
        setMuted(current.current.muted);
      }
    },
  };
}
